import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { catchError, from, Observable, of, switchMap } from 'rxjs';
import { FeatureFlagService } from '../feature-flags/feature-flag.service';
import {
  DEFAULT_LOGO_URL,
  DEFAULT_TENANT_CONFIG,
  TenantConfig,
  TenantTheme,
  TenantThemeColors,
  TenantThemeTypography,
} from './tenant-config.model';

/**
 * Service for managing tenant-specific configuration and branding.
 * Fetches configuration from the backend API and applies branding dynamically.
 *
 * Features:
 * - Fetches tenant config on app initialization
 * - Applies document title from tenant config
 * - Updates favicon and other icons dynamically
 * - Applies theme CSS custom properties for tenant-specific styling
 * - Provides fallback defaults if API is unavailable
 */
@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);
  private readonly featureFlags = inject(FeatureFlagService);

  private readonly apiUrl = 'api/tenant-config';

  // -------------------------------------------------------------------------
  // State signals
  // -------------------------------------------------------------------------

  private readonly _config = signal<TenantConfig>(DEFAULT_TENANT_CONFIG);
  private readonly _loading = signal<boolean>(false);
  private readonly _loaded = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  // -------------------------------------------------------------------------
  // Public readonly signals
  // -------------------------------------------------------------------------

  /** The current tenant configuration */
  readonly config = this._config.asReadonly();

  /** Whether the tenant config is currently being loaded */
  readonly loading = this._loading.asReadonly();

  /** Whether the tenant config has been successfully loaded */
  readonly loaded = this._loaded.asReadonly();

  /** Error message if tenant config failed to load */
  readonly error = this._error.asReadonly();

  // -------------------------------------------------------------------------
  // Computed signals for convenience
  // -------------------------------------------------------------------------

  /** The tenant's unique key (e.g., "PL", "LH") */
  readonly tenantKey = computed(() => this._config().tenantKey);

  /** The tenant's unique id. */
  readonly tenantId = computed(() => this._config().tenantId);

  /** The tenant's display name (e.g., "Principled Leadership", "Lighthouse Institute") */
  readonly displayName = computed(() => this._config().displayName);

  /** The current environment (e.g., "Prod", "Stage") */
  readonly environment = computed(() => this._config().environment);

  /** The application title for the current tenant */
  readonly appTitle = computed(() => this._config().appTitle);

  /** The branding resources dictionary */
  readonly brandingResources = computed(() => this._config().brandingResources);

  /** The base URL for branding assets */
  readonly brandingBaseUrl = computed(() => this._config().brandingBaseUrl);

  /** The tenant's logo URL (with fallback to default) */
  readonly logoUrl = computed(() => this.buildBrandingUrl('Logo', this._config()) ?? DEFAULT_LOGO_URL);

  /** The favicon.ico URL (undefined if not configured) */
  readonly faviconUrl = computed((): string | undefined => this.buildBrandingUrl('Favicon', this._config()));

  /** The 16x16 favicon URL (undefined if not configured - browser uses main favicon as fallback) */
  readonly favicon16Url = computed((): string | undefined => this.buildBrandingUrl('Favicon16', this._config()));

  /** The 32x32 favicon URL (undefined if not configured - browser uses main favicon as fallback) */
  readonly favicon32Url = computed((): string | undefined => this.buildBrandingUrl('Favicon32', this._config()));

  /** The Apple touch icon URL (undefined if not configured) */
  readonly appleTouchIconUrl = computed(
    (): string | undefined => this.buildBrandingUrl('AppleTouchIcon', this._config()),
  );

  /** The tenant's web manifest URL (undefined if not configured - no manifest link will be added) */
  readonly manifestUrl = computed((): string | undefined => this.buildBrandingUrl('Manifest', this._config()));

  /** The tenant's theme overrides (or null if using defaults) */
  readonly theme = computed(() => this._config().theme);

  /** Whether this is a production environment */
  readonly isProduction = computed(() => this._config().environment === 'Prod');

  /** Whether this is a staging environment */
  readonly isStaging = computed(() => this._config().environment === 'Stage');

  /** The report server URL for SSRS reports (null if not configured) */
  readonly reportServer = computed(() => this._config().reportServer);

  /** The tenant-specific report folder name (null if not configured) */
  readonly reportFolder = computed(() => this._config().reportFolder);

  // -------------------------------------------------------------------------
  // Initialization
  // -------------------------------------------------------------------------

  /**
   * Loads the tenant configuration from the API.
   * Should be called during app initialization via APP_INITIALIZER.
   * Waits for fonts to load before completing to prevent FOUT (Flash of Unstyled Text).
   * @returns Observable that completes when loading is done (including font loading)
   */
  loadConfig(forceRefresh = false): Observable<TenantConfig> {
    if (this._loaded() && !forceRefresh) {
      return of(this._config());
    }

    this._loading.set(true);
    this._error.set(null);

    const url = forceRefresh ? `${this.apiUrl}?_=${Date.now()}` : this.apiUrl;

    return this.http.get<TenantConfig>(url).pipe(
      switchMap((config) =>
        from(this.applyBrandingAsync(config)).pipe(
          switchMap(() => {
            this._config.set(config);
            this.featureFlags.setFlags(config.featureFlags);
            this._loaded.set(true);
            this._loading.set(false);
            return of(config);
          }),
        ),
      ),
      catchError((err) => {
        console.error('Failed to load tenant configuration:', err);
        this._error.set('Failed to load tenant configuration');
        this._loading.set(false);

        if (forceRefresh && this._loaded()) {
          return of(this._config());
        }

        this._loaded.set(true); // Mark as loaded even on error to prevent blocking

        // Apply default branding synchronously (no custom fonts)
        this.applyBranding(DEFAULT_TENANT_CONFIG);
        this.featureFlags.setFlags(DEFAULT_TENANT_CONFIG.featureFlags);

        return of(DEFAULT_TENANT_CONFIG);
      }),
    );
  }

  /**
   * Reloads tenant configuration even after app bootstrap. Used when admin
   * actions change the active tenant's feature-flag state.
   */
  refreshConfig(): Observable<TenantConfig> {
    return this.loadConfig(true);
  }

  // -------------------------------------------------------------------------
  // Branding Application
  // -------------------------------------------------------------------------

  /**
   * Applies all branding elements from the tenant configuration.
   * Gracefully skips applying resources that don't exist in the config.
   * The API may only return Logo and Favicon - other resources are optional.
   * This synchronous version is used for error fallback scenarios.
   *
   * @param config - The tenant configuration to apply
   */
  private applyBranding(config: TenantConfig): void {
    this.applyDocumentTitle(config.displayName);
    this.applyFavicons(config);
    this.applyManifest(config);
    this.applyThemeColors(config.theme);
    this.applyTypography(config.theme?.typography);
  }

  /**
   * Applies all branding elements from the tenant configuration asynchronously.
   * Waits for custom fonts to load before completing to prevent FOUT.
   *
   * @param config - The tenant configuration to apply
   * @returns Promise that resolves when all branding (including fonts) is applied
   */
  private async applyBrandingAsync(config: TenantConfig): Promise<void> {
    this.applyDocumentTitle(config.displayName);
    this.applyFavicons(config);
    this.applyManifest(config);

    // Apply colors immediately
    this.applyThemeColors(config.theme);

    // Wait for fonts to load (prevents FOUT)
    if (config.theme?.typography?.googleFonts?.length) {
      await this.loadGoogleFonts(config.theme.typography);
    }

    // Apply typography CSS variables
    this.applyTypography(config.theme?.typography);
  }

  /**
   * Updates the document title with the tenant's app title.
   * @param title - The new document title
   */
  private applyDocumentTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  /**
   * Updates the favicon and related icons dynamically from branding resources.
   * Only creates/updates link elements for resources that exist.
   * The browser will use the main favicon.ico as fallback for missing sized icons.
   * Uses buildBrandingUrl to construct full URLs from base URL, tenant key, and version.
   *
   * @param config - The tenant configuration to use for building URLs
   */
  private applyFavicons(config: TenantConfig): void {
    // Update favicon.ico (standard) - only if configured
    const faviconUrl = this.buildBrandingUrl('Favicon', config);
    if (faviconUrl) {
      this.updateLink('icon', faviconUrl, 'image/x-icon');
    }

    // Update 16x16 favicon - only if configured (browser uses main favicon as fallback)
    const favicon16Url = this.buildBrandingUrl('Favicon16', config);
    if (favicon16Url) {
      this.updateLink('icon', favicon16Url, 'image/png', 'favicon-16', '16x16');
    }

    // Update 32x32 favicon - only if configured (browser uses main favicon as fallback)
    const favicon32Url = this.buildBrandingUrl('Favicon32', config);
    if (favicon32Url) {
      this.updateLink('icon', favicon32Url, 'image/png', 'favicon-32', '32x32');
    }

    // Update Apple touch icon - only if configured
    const appleTouchIconUrl = this.buildBrandingUrl('AppleTouchIcon', config);
    if (appleTouchIconUrl) {
      this.updateLink('apple-touch-icon', appleTouchIconUrl);
    }
  }

  /**
   * Updates the web manifest link dynamically.
   * Only creates/updates the manifest link if the URL exists.
   * If no manifest URL is configured, no manifest link is added.
   * Uses buildBrandingUrl to construct full URL from base URL, tenant key, and version.
   *
   * @param config - The tenant configuration to use for building URLs
   */
  private applyManifest(config: TenantConfig): void {
    const manifestUrl = this.buildBrandingUrl('Manifest', config);

    // Only add manifest link if URL is configured
    if (!manifestUrl) {
      return;
    }

    this.updateLink('manifest', manifestUrl);
  }

  /**
   * Applies theme color CSS custom properties from tenant configuration.
   * Supports both nested (theme.colors) and flat (theme.primary, etc.) structures
   * for backward compatibility.
   *
   * @param theme - Theme overrides or null for defaults
   */
  private applyThemeColors(theme: TenantTheme | null): void {
    if (!theme) {
      return;
    }

    const root = this.document.documentElement;

    // Support both nested colors and flat properties for backward compatibility
    const colors: TenantThemeColors = theme.colors ?? theme;

    // Map color properties to CSS custom properties
    const colorMapping: Array<[keyof TenantThemeColors, string]> = [
      ['primary', '--tenant-primary'],
      ['onPrimary', '--tenant-on-primary'],
      ['primaryContainer', '--tenant-primary-container'],
      ['onPrimaryContainer', '--tenant-on-primary-container'],
      ['secondary', '--tenant-secondary'],
      ['onSecondary', '--tenant-on-secondary'],
      ['secondaryContainer', '--tenant-secondary-container'],
      ['onSecondaryContainer', '--tenant-on-secondary-container'],
      ['tertiary', '--tenant-tertiary'],
      ['onTertiary', '--tenant-on-tertiary'],
      ['tertiaryContainer', '--tenant-tertiary-container'],
      ['onTertiaryContainer', '--tenant-on-tertiary-container'],
      ['error', '--tenant-error'],
      ['onError', '--tenant-on-error'],
    ];

    colorMapping.forEach(([colorKey, cssVar]) => {
      const value = colors[colorKey];
      if (value) {
        root.style.setProperty(cssVar, value);
      }
    });
  }

  /**
   * Loads Google Fonts and waits for them to be ready.
   * Uses the Font Loading API with a timeout to prevent blocking indefinitely.
   *
   * @param typography - Typography configuration with googleFonts array
   * @returns Promise that resolves when fonts are loaded or timeout occurs
   */
  private async loadGoogleFonts(typography: TenantThemeTypography): Promise<void> {
    const fonts = typography.googleFonts;
    if (!fonts?.length) {
      return;
    }

    // Build Google Fonts URL
    // Format: https://fonts.googleapis.com/css2?family=Font1:wght@400;700&family=Font2:wght@300&display=swap
    const fontUrl = `https://fonts.googleapis.com/css2?${fonts.map((f) => `family=${encodeURIComponent(f)}`).join('&')}&display=swap`;

    // Remove any existing tenant font link
    const existingLink = this.document.head.querySelector('link[data-tenant-fonts]');
    if (existingLink) {
      existingLink.remove();
    }

    // Add link element for Google Fonts stylesheet
    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    link.setAttribute('data-tenant-fonts', 'true');
    this.document.head.appendChild(link);

    // Wait for fonts to load using Font Loading API
    try {
      const fontPromises: Promise<FontFace[]>[] = [];

      // Extract font family name from the CSS font-family string
      // e.g., "'Merriweather', Georgia, serif" -> "Merriweather"
      const extractFontName = (fontFamily: string): string => {
        return fontFamily.split(',')[0].replace(/'/g, '').trim();
      };

      if (typography.headingFontFamily) {
        const headingFont = extractFontName(typography.headingFontFamily);
        fontPromises.push(this.document.fonts.load(`400 1em "${headingFont}"`));
      }

      if (typography.bodyFontFamily) {
        const bodyFont = extractFontName(typography.bodyFontFamily);
        fontPromises.push(this.document.fonts.load(`400 1em "${bodyFont}"`));
      }

      // Wait for fonts with 3 second timeout to prevent blocking indefinitely
      const timeoutPromise = new Promise<void>((resolve) => setTimeout(resolve, 3000));

      await Promise.race([Promise.all(fontPromises), timeoutPromise]);
    } catch {
      // Fonts failed to load - continue with fallback fonts
      console.warn('Failed to load tenant fonts, using fallback fonts');
    }
  }

  /**
   * Applies typography CSS custom properties from tenant configuration.
   *
   * @param typography - Typography configuration or null/undefined for defaults
   */
  private applyTypography(typography: TenantThemeTypography | null | undefined): void {
    if (!typography) {
      return;
    }

    const root = this.document.documentElement;

    if (typography.headingFontFamily) {
      root.style.setProperty('--tenant-heading-font', typography.headingFontFamily);
    }

    if (typography.bodyFontFamily) {
      root.style.setProperty('--tenant-body-font', typography.bodyFontFamily);
    }
  }

  // -------------------------------------------------------------------------
  // URL Builders
  // -------------------------------------------------------------------------

  /**
   * Builds a full branding URL from the resource key.
   * Format: {brandingBaseUrl}/{tenantKey}/v{version}/{resourceUrl}
   * Example: https://assets.frontlinesolutions.com/branding/PL/v1/logo.svg
   *
   * Returns undefined if:
   * - The resource is not configured in brandingResources
   * - The brandingBaseUrl is not configured (empty/null/undefined)
   *
   * This allows callers to fall back to default local assets when branding
   * is not fully configured.
   *
   * @param resourceKey - The key of the branding resource (e.g., 'Logo', 'Favicon')
   * @param config - The tenant configuration to use for building the URL
   * @returns The full URL or undefined if resource or base URL is not configured
   */
  private buildBrandingUrl(resourceKey: string, config: TenantConfig): string | undefined {
    const resource = config.brandingResources[resourceKey];

    // If no resource configured, return undefined to trigger fallback
    if (!resource) {
      return undefined;
    }

    // If no brandingBaseUrl configured, return undefined to trigger fallback
    // The raw resourceUrl alone (e.g., "pl-logo-tree.png") is not a valid path
    if (!config.brandingBaseUrl) {
      return undefined;
    }

    // Build full URL: {baseUrl}/{tenantKey}/v{version}/{resourceUrl}
    return `${config.brandingBaseUrl}/${config.tenantKey}/v${resource.version}/${resource.resourceUrl}`;
  }

  // -------------------------------------------------------------------------
  // DOM Helpers
  // -------------------------------------------------------------------------

  /**
   * Updates or creates a link element in the document head.
   * @param rel - The rel attribute value
   * @param href - The href attribute value
   * @param type - Optional type attribute
   * @param id - Optional id for the link element
   * @param sizes - Optional sizes attribute (for icons)
   */
  private updateLink(
    rel: string,
    href: string,
    type?: string,
    id?: string,
    sizes?: string,
  ): void {
    const head = this.document.head;

    // Try to find existing link by id or rel+type combination
    let linkSelector = id ? `#${id}` : `link[rel="${rel}"]`;
    if (type && !id) {
      linkSelector += `[type="${type}"]`;
    }
    if (sizes && !id) {
      linkSelector += `[sizes="${sizes}"]`;
    }

    let link = head.querySelector<HTMLLinkElement>(linkSelector);

    if (!link) {
      // Create new link element
      link = this.document.createElement('link');
      link.rel = rel;
      if (id) {
        link.id = id;
      }
      if (type) {
        link.type = type;
      }
      if (sizes) {
        link.setAttribute('sizes', sizes);
      }
      head.appendChild(link);
    }

    link.href = href;
  }
}
